import React, { useEffect, useState, useCallback, useRef } from "react"
import { createPortal } from "react-dom"
import { useNavigate, useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import * as XLSX from "xlsx"
import * as FileSaver from "file-saver"
import { useAppDispatch, useAppSelector } from "../../../redux/store"
import {
  deleteGuest,
  getContactsOnEvent,
  loadMoreContacts,
  setCurrentPage,
  setLoading,
} from "../../../redux/slices/contacts/contactsSlices"
import { getEvent, sendWAInvite } from "../../../redux/slices/events/eventsSlice"
import {
  getConversation,
  getMessagesWithParams,
  markAsRead,
  setNewUser,
} from "../../../redux/slices/messages/messagesSlice"
import { IEvent } from "../../../components/Events/types"
import { IContact } from "../../../components/Contacts/types"
import { useInfiniteScroll } from "../hooks/useInfiniteScroll"
import {
  type InvitationStatusFilter,
  matchesInvitationFilter,
} from "../constants/filters"
import { fetchAllEventContacts } from "../utils/fetchAllEventContacts"
import { invitationStatsFromEvent } from "../utils/invitationStatsFromEvent"
import styles from "../EventDashboardV2.module.scss"

export type StatusFilter = InvitationStatusFilter

interface FilterTab {
  id: StatusFilter
  label: string
  count: number
  statusNumber: string
  waStatusNumber: string
  failureType?: "contact" | "system"
}

interface GuestListProps {
  event: IEvent
  activeFilter: StatusFilter
  onFilterChange: (filter: StatusFilter) => void
  editPopupOpen: (guest: any) => void
  onRowClick: (guest: any) => void
  filter: any
  notify: (text: string, isError: boolean) => void
  checked: any
  setChecked: (v: any) => void
}

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
)

const EditSmallIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
)

const TrashSmallIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
)

const DownloadIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
)

const SendIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
)

const MessageSmallIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15a4 4 0 0 1-4 4H7l-4 4V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
  </svg>
)

const CheckCircleIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
)

const SpinnerIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={styles.spinner}>
    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
  </svg>
)

interface DeleteButtonProps {
  guestId: number
  guestName: string
  eventId: string
  filter: any
  notify: (text: string, isError: boolean) => void
}

const DeleteButton: React.FC<DeleteButtonProps> = ({ guestId, guestName, eventId, filter, notify }) => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const [confirming, setConfirming] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const btnRef = useRef<HTMLButtonElement>(null)
  const popRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState({ top: 0, left: 0 })

  useEffect(() => {
    if (!confirming) return
    const onClickOutside = (e: MouseEvent) => {
      if (
        popRef.current && !popRef.current.contains(e.target as Node) &&
        btnRef.current && !btnRef.current.contains(e.target as Node)
      ) {
        setConfirming(false)
      }
    }
    document.addEventListener("mousedown", onClickOutside)
    return () => document.removeEventListener("mousedown", onClickOutside)
  }, [confirming])

  const openConfirm = () => {
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect()
      setPos({
        top: rect.bottom + 6 + window.scrollY,
        left: rect.right - 240 + window.scrollX,
      })
    }
    setConfirming(true)
  }

  const handleDelete = () => {
    setDeleting(true)
    dispatch(deleteGuest({ eventId, guestId: [{ contact_id: guestId }] }))
      .then((result: any) => {
        if (result.error) {
          notify(t("eventDashboardV2.messages.failedRemoveGuest"), true)
        } else {
          dispatch(setLoading())
          dispatch(getContactsOnEvent(filter))
          notify(t("eventDashboardV2.messages.guestRemoved"), false)
        }
        setDeleting(false)
        setConfirming(false)
      })
      .catch(() => {
        notify(t("eventDashboardV2.messages.somethingWentWrong"), true)
        setDeleting(false)
        setConfirming(false)
      })
  }

  return (
    <>
      <button
        ref={btnRef}
        className={`${styles.actionIconBtn} ${styles.actionIconBtnDanger}`}
        title={t("eventDashboardV2.actions.remove")}
        onClick={openConfirm}
      >
        <TrashSmallIcon />
      </button>
      {confirming && createPortal(
        <div
          ref={popRef}
          className={styles.deleteConfirm}
          style={{ top: pos.top, left: pos.left }}
        >
          <p className={styles.deleteConfirmText}>
            {t("eventDashboardV2.messages.confirmRemoveGuest", { name: guestName })}
          </p>
          <div className={styles.deleteConfirmActions}>
            <button
              type="button"
              className={styles.deleteConfirmCancel}
              onClick={() => setConfirming(false)}
              disabled={deleting}
            >
              {t("eventDashboardV2.actions.cancel")}
            </button>
            <button
              type="button"
              className={styles.deleteConfirmBtn}
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? t("eventDashboardV2.messages.removing") : t("eventDashboardV2.actions.remove")}
            </button>
          </div>
        </div>,
        document.body,
      )}
    </>
  )
}

const WhatsAppSmallIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
)

const MailSmallIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
)

export const getUnifiedStatus = (
  rsvpStatus: number,
  waStatus: number,
  invitationFailureType?: "contact" | "system" | null,
  isOpenEvent: boolean = false,
): { text: string; variant: string; dotColor: string } => {
  // RSVP statuses take priority — they represent the final guest decision
  if (rsvpStatus === 1) return { text: "Confirmed", variant: "Confirmed", dotColor: "Green" }
  if (rsvpStatus === 2) return { text: "Declined", variant: "Declined", dotColor: "Red" }
  if (rsvpStatus === 3) return { text: "Invalid RSVP", variant: "Issue", dotColor: "Red" }
  if (rsvpStatus === 4) return { text: "Scanned", variant: "Confirmed", dotColor: "Green" }

  // WhatsApp delivery pipeline
  switch (waStatus) {
    case 1: return { text: "Sent", variant: "Sent", dotColor: "Blue" }
    case 2: return { text: "Delivered", variant: "Delivered", dotColor: "Green" }
    case 3: return { text: "Received", variant: "Delivered", dotColor: "Green" }
    case 4: return { text: "Read", variant: "Delivered", dotColor: "Green" }
    case 5:
      // Split failed invitations so hosts can tell "we need a better number"
      // from "the platform hiccupped, retry". Unknown failures are classed
      // as system so nothing disappears.
      if (invitationFailureType === "contact") {
        return { text: "Invalid contact", variant: "Issue", dotColor: "Red" }
      }
      return { text: "Failed", variant: "Issue", dotColor: "Orange" }
    case 6: return { text: "Deleted", variant: "NotSent", dotColor: "Gray" }
    default:
      // For open events, the pre-invite state (waStatus=0, rsvpStatus=0)
      // means the guest completed the public registration form and is
      // waiting for the host to send the QR. "Not sent" reads like a
      // mistake from the guest's perspective — "Registered" tells the
      // real story.
      if (isOpenEvent) {
        return { text: "Registered", variant: "Sent", dotColor: "Blue" }
      }
      return { text: "Not sent", variant: "NotSent", dotColor: "Gray" }
  }
}

const GuestList: React.FC<GuestListProps> = ({
  event,
  activeFilter,
  onFilterChange,
  editPopupOpen,
  onRowClick,
  filter: parentFilter,
  notify,
  checked,
  setChecked,
}) => {
  const { event_id } = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { data_uploaded, data_on_event, current_page, total_pages, limit, isLoading } =
    useAppSelector((state) => state.contacts)

  const [searchValue, setSearchValue] = useState("")
  const [sendPhase, setSendPhase] = useState<"idle" | "confirm" | "sending" | "success">("idle")
  const [sendResult, setSendResult] = useState({ sent: 0, skipped: 0 })
  const [isExporting, setIsExporting] = useState(false)

  const {
    waUnsent,
    waSent,
    total,
    going,
    declined,
    invalidRsvp,
    scanned,
    awaitingReply,
    failedContact,
    failedSystem,
  } = invitationStatsFromEvent(event)

  const filterTabs: FilterTab[] = [
    { id: "all", label: "All", count: total, statusNumber: "", waStatusNumber: "" },
    { id: "not_sent", label: "Not Sent Yet", count: waUnsent, statusNumber: "", waStatusNumber: "0" },
    { id: "confirmed", label: "Confirmed", count: going, statusNumber: "1", waStatusNumber: "" },
    { id: "awaiting_reply", label: "Awaiting reply", count: awaitingReply, statusNumber: "", waStatusNumber: "2,3,4" },
    { id: "in_progress", label: "In Progress", count: waSent, statusNumber: "", waStatusNumber: "1" },
    { id: "invalid", label: "Invalid RSVP", count: invalidRsvp, statusNumber: "3", waStatusNumber: "" },
    {
      id: "failed_contact",
      label: "Invalid contacts",
      count: failedContact,
      statusNumber: "",
      waStatusNumber: "5",
      failureType: "contact",
    },
    {
      id: "failed_system",
      label: "Failed",
      count: failedSystem,
      statusNumber: "",
      waStatusNumber: "5",
      failureType: "system",
    },
    { id: "scanned", label: "Scanned", count: scanned, statusNumber: "4", waStatusNumber: "" },
    { id: "declined", label: "Declined", count: declined, statusNumber: "2", waStatusNumber: "" },
  ]

  const activeTab = filterTabs.find((t) => t.id === activeFilter) ?? filterTabs[0]

  const filterParams = useCallback(() => ({
    sort: "",
    order: "",
    searchValue,
    id: Number(event_id),
    statusNumber: activeTab.statusNumber,
    waStatusNumber: activeTab.waStatusNumber,
    reminderNumber: "",
  }), [searchValue, event_id, activeTab.statusNumber, activeTab.waStatusNumber])

  const fetchGuests = useCallback(() => {
    dispatch(setCurrentPage(1))
    dispatch(getContactsOnEvent(filterParams()))
  }, [dispatch, filterParams])

  useEffect(() => {
    fetchGuests()
  }, [fetchGuests])

  const handleLoadMore = useCallback(async (page: number) => {
    await dispatch(loadMoreContacts({ page, params: filterParams() }))
  }, [dispatch, filterParams])

  const { sentinelRef, isFetchingMore, hasMore, reset } = useInfiniteScroll({
    totalPages: total_pages,
    onLoadMore: handleLoadMore,
  })

  const handleFilterChange = (filterId: StatusFilter) => {
    onFilterChange(filterId)
    reset()
  }

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value)
    reset()
  }

  // Invitation filters are applied both at the API layer (statusNumber /
  // waStatusNumber) and client-side here — because failureType for the two
  // "Failed" buckets has no backend filter representation, it must be applied
  // after fetching. matchesInvitationFilter handles both cases uniformly.
  const displayedGuests = data_uploaded.filter((g: any) =>
    matchesInvitationFilter(g, {
      id: activeTab.id,
      label: activeTab.label,
      statusNumber: activeTab.statusNumber,
      waStatusNumber: activeTab.waStatusNumber,
      failureType: activeTab.failureType,
    }),
  )

  const allSelected =
    displayedGuests.length > 0 &&
    displayedGuests.every((g: any) => checked.includes(g.id))

  const handleSelectAll = () => {
    if (allSelected) {
      setChecked([])
    } else {
      setChecked(displayedGuests.map((g: any) => g.id))
    }
  }

  const handleSelectRow = (id: number) => {
    if (checked.includes(id)) {
      setChecked(checked.filter((cId: number) => cId !== id))
    } else {
      setChecked([...checked, id])
    }
  }

  const getContactDisplay = (guest: any) => {
    const phone = guest.phone ? guest.phone.split("|")[0] : ""
    const hasEmail = !!guest.email
    const channelCount = (phone ? 1 : 0) + (hasEmail ? 1 : 0)
    return { phone, email: guest.email, channelCount }
  }

  const getInviteCount = (guest: IContact) => {
    const actual = guest.actual_additional_guests ?? 0
    const max = guest.quantity_members ?? 1
    return t("eventDashboardV2.messages.inviteCountProgress", {
      current: actual + 1,
      max: max + 1,
    })
  }

  const openMessages = (guest: any) => {
    dispatch(getMessagesWithParams()).then(() => {
      dispatch(
        setNewUser({
          eventId: Number(event_id),
          contactId: guest.id,
          first_name: guest.first_name,
          last_name: guest.last_name || "",
          suffix: guest.suffix,
          email: guest.email,
          phone: guest.phone,
        }),
      )
    }).then(() => {
      dispatch(getConversation(guest.id))
      dispatch(markAsRead(guest.id))
      navigate("/messages")
    })
  }

  const localFilter = {
    sort: "",
    order: "",
    searchValue,
    statusNumber: activeTab.statusNumber,
    waStatusNumber: activeTab.waStatusNumber,
    reminderNumber: "",
    id: Number(event_id),
  }

  // --- Send invitation logic ---
  const hasSelection = checked.length > 0

  const getEligibleGuests = useCallback(() => {
    const pool = hasSelection
      ? displayedGuests.filter((g: any) => checked.includes(g.id))
      : displayedGuests

    const eligible = pool.filter((g: any) => g.can_send_invitation === true)
    const skipped = pool.length - eligible.length
    return { eligible, skipped, total: pool.length }
  }, [displayedGuests, checked, hasSelection])

  const { eligible: eligibleGuests, skipped: skippedCount, total: targetTotal } = getEligibleGuests()

  const handleSendClick = () => {
    if (eligibleGuests.length === 0) {
      notify(t("eventDashboardV2.messages.noEligibleGuests"), true)
      return
    }
    setSendPhase("confirm")
  }

  const handleSendConfirm = () => {
    setSendPhase("sending")
    const contactIds = eligibleGuests.map((g: any) => g.id)

    dispatch(sendWAInvite({ eventId: Number(event_id), contactIds }))
      .then((result: any) => {
        if (result.error) {
          notify(t("eventDashboardV2.messages.failedSendInvitations"), true)
          setSendPhase("idle")
        } else {
          setSendResult({ sent: contactIds.length, skipped: skippedCount })
          setSendPhase("success")
          setChecked([])
          dispatch(setLoading())
          dispatch(getEvent(Number(event_id)))
          fetchGuests()
          setTimeout(() => setSendPhase("idle"), 3000)
        }
      })
      .catch(() => {
        notify(t("eventDashboardV2.messages.somethingWentWrong"), true)
        setSendPhase("idle")
      })
  }

  const handleSendCancel = () => {
    setSendPhase("idle")
  }

  const buildExportRows = (source: any[]) =>
    source.map((g: any) => {
      const name = [g.suffix, g.first_name, g.last_name].filter(Boolean).join(" ")
      const phone = g.phone ? g.phone.split("|")[0] : ""
      const status = getUnifiedStatus(
        g.status,
        g.whatsapp_invitation_status ?? 0,
        g.invitation_failure_type,
        event.is_open_event === 1,
      )
      const rsvp =
        g.status === 1
          ? t("eventDashboardV2.status.Confirmed")
          : g.status === 2
            ? t("eventDashboardV2.status.Declined")
            : g.status === 3
              ? t("eventDashboardV2.status.Invalid RSVP")
              : "—"
      const actual = g.actual_additional_guests ?? 0
      const max = g.quantity_members ?? 1

      return {
        [t("eventDashboardV2.messages.exportColId")]: g.id,
        [t("eventDashboardV2.messages.exportColName")]: name,
        [t("eventDashboardV2.messages.exportColPhone")]: phone,
        [t("eventDashboardV2.messages.exportColEmail")]: g.email || "",
        [t("eventDashboardV2.messages.exportColStatus")]: t(
          `eventDashboardV2.status.${status.text}`,
          status.text,
        ),
        [t("eventDashboardV2.messages.exportColRsvp")]: rsvp,
        [t("eventDashboardV2.messages.exportColInvites")]: t(
          "eventDashboardV2.messages.inviteCountProgress",
          { current: actual + 1, max: max + 1 },
        ),
        [t("eventDashboardV2.messages.exportColZone")]: g.zone || "",
      }
    })

  const saveExportRows = (rows: Record<string, unknown>[]) => {
    const ws = XLSX.utils.json_to_sheet(rows)
    const sheetName = t("eventDashboardV2.messages.exportSheetName")
    const wb = { Sheets: { [sheetName]: ws }, SheetNames: [sheetName] }
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" })
    const blob = new Blob([buf], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    })
    FileSaver.saveAs(blob, `${t("eventDashboardV2.messages.exportSheetName")}.xlsx`)
  }

  const handleExport = async () => {
    if (isExporting) return

    setIsExporting(true)
    try {
      let source: any[]
      if (hasSelection) {
        source = displayedGuests.filter((g: any) => checked.includes(g.id))
      } else {
        const allGuests = await fetchAllEventContacts(filterParams())
        source = allGuests.filter((g: any) =>
          matchesInvitationFilter(g, {
            id: activeTab.id,
            label: activeTab.label,
            statusNumber: activeTab.statusNumber,
            waStatusNumber: activeTab.waStatusNumber,
            failureType: activeTab.failureType,
          }),
        )
      }

      if (source.length === 0) {
        notify(t("eventDashboardV2.messages.noGuestsToExport"), true)
        return
      }

      saveExportRows(buildExportRows(source))
    } catch {
      notify(t("eventDashboardV2.messages.somethingWentWrong"), true)
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <div className={styles.guestListSection}>
      {/* Success banner */}
      {sendPhase === "success" && (
        <div className={styles.sendSuccessBanner}>
          <CheckCircleIcon />
          <span>
              {t("eventDashboardV2.messages.invitationsSent", { count: sendResult.sent })}
            {sendResult.skipped > 0 && (
              <span className={styles.sendSkippedNote}>
                {" "}&middot; {t("eventDashboardV2.messages.skippedIneligible", { count: sendResult.skipped })}
              </span>
            )}
          </span>
        </div>
      )}

      <div className={styles.guestListHeader}>
        <div className={styles.searchInput}>
          <SearchIcon />
          <input
            type="text"
            placeholder={t("eventDashboardV2.actions.search")}
            value={searchValue}
            onChange={handleSearch}
          />
        </div>
        {!hasSelection && sendPhase !== "confirm" && sendPhase !== "sending" && (
          <div className={styles.guestListControls}>
            <button
              className={styles.controlBtn}
              title={t("eventDashboardV2.actions.exportToExcel")}
              onClick={handleExport}
              disabled={isExporting}
            >
              <DownloadIcon />
            </button>
            <button className={styles.sendInvitationBtn} onClick={handleSendClick}>
              <SendIcon />
              {t("eventDashboardV2.actions.sendInvitation")}
            </button>
          </div>
        )}
      </div>

      {/* Inline confirmation (no selection — header-level) */}
      {!hasSelection && (sendPhase === "confirm" || sendPhase === "sending") && (
        <div className={styles.sendConfirmBar}>
          <div className={styles.sendConfirmInfo}>
            <span className={styles.sendConfirmText}>
              {t("eventDashboardV2.messages.sendInvitationsTo", { count: eligibleGuests.length })}
            </span>
            {skippedCount > 0 && (
              <span className={styles.sendConfirmSkipped}>
                {t("eventDashboardV2.messages.willBeSkipped", { count: skippedCount })}
              </span>
            )}
          </div>
          <div className={styles.sendConfirmActions}>
            <button
              type="button"
              className={styles.sendConfirmCancel}
              onClick={handleSendCancel}
              disabled={sendPhase === "sending"}
            >
              {t("eventDashboardV2.actions.cancel")}
            </button>
            <button
              type="button"
              className={styles.sendConfirmBtn}
              onClick={handleSendConfirm}
              disabled={sendPhase === "sending"}
            >
              {sendPhase === "sending" ? <><SpinnerIcon /> {t("eventDashboardV2.actions.sending")}</> : t("eventDashboardV2.actions.confirmAndSend")}
            </button>
          </div>
        </div>
      )}

      <div className={styles.guestListTabs}>
        {filterTabs.map((tab) => (
          <button
            key={tab.id}
            className={`${styles.guestListTab} ${
              activeFilter === tab.id ? styles.guestListTabActive : ""
            }`}
            onClick={() => handleFilterChange(tab.id)}
          >
            {t(`eventDashboardV2.filters.${tab.id}`, tab.label)} ({tab.count})
          </button>
        ))}
      </div>

      <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead className={styles.tableHead}>
          <tr>
            <th className={styles.checkboxCell}>
              <input
                type="checkbox"
                className={styles.checkbox}
                checked={allSelected}
                onChange={handleSelectAll}
              />
            </th>
            <th className={styles.colId}>{t("eventDashboardV2.table.id")}</th>
            <th className={styles.colName}>{t("eventDashboardV2.table.guestName")}</th>
            <th className={styles.colContact}>{t("eventDashboardV2.table.contact")}</th>
            <th className={styles.colStatus}>{t("eventDashboardV2.table.status")}</th>
            <th className={styles.colInvites}>{t("eventDashboardV2.table.invites")}</th>
            <th className={styles.colActions}>{t("eventDashboardV2.table.actions")}</th>
          </tr>
        </thead>
        <tbody className={styles.tableBody}>
          {isLoading
            ? Array.from({ length: 3 }).map((_, i) => (
                <tr key={`skel-${i}`}>
                  <td className={styles.checkboxCell}>
                    <div className={styles.skeleton} style={{ width: 16, height: 16 }} />
                  </td>
                  <td><div className={styles.skeleton} style={{ width: 48 }} /></td>
                  <td><div className={styles.skeleton} style={{ width: '70%' }} /></td>
                  <td><div className={styles.skeleton} style={{ width: '60%' }} /></td>
                  <td><div className={styles.skeleton} style={{ width: '50%' }} /></td>
                  <td><div className={styles.skeleton} style={{ width: '40%' }} /></td>
                  <td>
                    <div className={styles.actionsCell}>
                      <div className={styles.skeleton} style={{ width: 32, height: 32 }} />
                      <div className={styles.skeleton} style={{ width: 32, height: 32 }} />
                    </div>
                  </td>
                </tr>
              ))
            : displayedGuests.map((guest: any) => {
                const contact = getContactDisplay(guest)
                const status = getUnifiedStatus(
                  guest.status,
                  guest.whatsapp_invitation_status ?? 0,
                  guest.invitation_failure_type,
                  event.is_open_event === 1,
                )

                return (
                  <tr
                    key={guest.id}
                    className={styles.clickableRow}
                    onClick={() => onRowClick(guest)}
                  >
                    <td
                      className={styles.checkboxCell}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        className={styles.checkbox}
                        checked={checked.includes(guest.id)}
                        onChange={() => handleSelectRow(guest.id)}
                      />
                    </td>
                    <td>{guest.id}</td>
                    <td className={styles.guestName}>
                      {[guest.suffix, guest.first_name, guest.last_name]
                        .filter(Boolean)
                        .join(" ")}
                    </td>
                    <td>
                      <div className={styles.contactCell}>
                        {contact.phone ? (
                          <>
                            <WhatsAppSmallIcon />
                            <span>{contact.phone}</span>
                          </>
                        ) : contact.email ? (
                          <>
                            <MailSmallIcon />
                            <span>{contact.email}</span>
                          </>
                        ) : (
                          "—"
                        )}
                        {contact.channelCount > 1 && (
                          <span className={styles.channelChip}>
                            +{contact.channelCount - 1}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span
                        className={`${styles.statusBadge} ${styles[`statusBadge${status.variant}`]}`}
                      >
                        <span
                          className={`${styles.statusDot} ${styles[`statusDot${status.dotColor}`]}`}
                        />
                        {t(`eventDashboardV2.status.${status.text}`, status.text)}
                      </span>
                    </td>
                    <td>{getInviteCount(guest)}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className={styles.actionsCell}>
                        <button
                          className={styles.actionIconBtn}
                          title={t("eventDashboardV2.actions.edit")}
                          onClick={() => editPopupOpen(guest)}
                        >
                          <EditSmallIcon />
                        </button>
                        <button
                          className={styles.actionIconBtn}
                          title={t("eventDashboardV2.actions.message")}
                          onClick={() => openMessages(guest)}
                        >
                          <MessageSmallIcon />
                        </button>
                        <DeleteButton
                          guestId={guest.id}
                          guestName={[guest.first_name, guest.last_name].filter(Boolean).join(" ") || t("eventDashboardV2.messages.thisGuest")}
                          eventId={event_id!}
                          filter={localFilter}
                          notify={notify}
                        />
                      </div>
                    </td>
                  </tr>
                )
              })}
        </tbody>
      </table>
      </div>

      {displayedGuests.length === 0 && !isLoading && (
        <div className={styles.emptyState}>{t("eventDashboardV2.messages.noGuestsFound")}</div>
      )}

      {isFetchingMore && (
        <div className={styles.loadMoreRow}>
          <div className={styles.skeleton} style={{ width: "100%", height: 40 }} />
        </div>
      )}
      {hasMore && <div ref={sentinelRef} className={styles.scrollSentinel} />}

      {/* Floating selection action bar */}
      {hasSelection && sendPhase !== "success" && (
        <div className={styles.selectionBar}>
          {sendPhase === "idle" && (
            <>
              <div className={styles.selectionBarLeft}>
                <span className={styles.selectionBadge}>{checked.length}</span>
                <span className={styles.selectionLabel}>
                  {t("eventDashboardV2.messages.guestsSelected", { count: checked.length })}
                </span>
                <button
                  type="button"
                  className={styles.selectionClear}
                  onClick={() => setChecked([])}
                >
                  {t("eventDashboardV2.actions.clear")}
                </button>
              </div>
              <div className={styles.selectionBarRight}>
                <button
                  type="button"
                  className={styles.selectionExportBtn}
                  title={t("eventDashboardV2.actions.exportSelected")}
                  onClick={handleExport}
                >
                  <DownloadIcon />
                </button>
                <button
                  type="button"
                  className={styles.selectionSendBtn}
                  onClick={handleSendClick}
                >
                  <SendIcon />
                  {t("eventDashboardV2.actions.sendInvitation")}
                </button>
              </div>
            </>
          )}

          {(sendPhase === "confirm" || sendPhase === "sending") && (
            <>
              <div className={styles.selectionBarLeft}>
                <span className={styles.sendConfirmText}>
                  {t("eventDashboardV2.messages.sendSelected", { eligible: eligibleGuests.length, selected: checked.length })}
                </span>
                {skippedCount > 0 && (
                  <span className={styles.sendConfirmSkipped}>
                    &middot; {t("eventDashboardV2.messages.skipped", { count: skippedCount })}
                  </span>
                )}
              </div>
              <div className={styles.sendConfirmActions}>
                <button
                  type="button"
                  className={styles.sendConfirmCancel}
                  onClick={handleSendCancel}
                  disabled={sendPhase === "sending"}
                >
                  {t("eventDashboardV2.actions.cancel")}
                </button>
                <button
                  type="button"
                  className={styles.sendConfirmBtn}
                  onClick={handleSendConfirm}
                  disabled={sendPhase === "sending"}
                >
                  {sendPhase === "sending" ? <><SpinnerIcon /> {t("eventDashboardV2.actions.sending")}</> : t("eventDashboardV2.actions.confirmAndSend")}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default GuestList
